import { addDays, format } from "date-fns";
import {
    RECOGNITION_METHOD_LABELS,
    SINGLE_TERM_ROW_MESSAGE,
} from "@common/constants/domains/accounting/schedules";
import { SCHEDULE_MILESTONE_COMPONENT_ALONE_MESSAGE } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import {
    isDailyProRataTermUnit,
    isTermRecognitionMethod,
    RecognitionAnchor,
    RecognitionMethod,
    ScheduleMilestoneAnchor,
    ScheduleMilestoneOffsetUnit,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import type { PickerItemData } from "@design-system/Picker/Picker.types";

export const SOURCE_DATE_LABEL = "Source date";

export const ALLOCATION_METHOD_CONNECTOR_LABEL = "as";

export const PERIOD_COUNT_CONNECTOR_LABEL = "for";

export const SOURCE_DATE_TOOLTIP =
    "The date of the transaction or invoice this schedule is applied to. For a source applied manually, the date entered when the schedule is applied.";

export const SPECIFIC_DATE_TOOLTIP =
    "The date entered when this schedule is applied, independent of the transaction or invoice date.";

export type DateAnchorOption = "sourceDate" | "specificDate";

export const DATE_ANCHOR_OPTIONS: ReadonlyArray<{
    value: DateAnchorOption;
    label: string;
    tooltip: string;
}> = [
    {
        value: "sourceDate",
        label: SOURCE_DATE_LABEL,
        tooltip: SOURCE_DATE_TOOLTIP,
    },
    {
        value: "specificDate",
        label: "Specific date",
        tooltip: SPECIFIC_DATE_TOOLTIP,
    },
];

export const MILESTONE_ANCHOR_LABELS: Record<ScheduleMilestoneAnchor, string> =
    {
        [ScheduleMilestoneAnchor.sourceDate]: "Source date",
        [ScheduleMilestoneAnchor.specificDate]: "Specific date",
    };

export const MILESTONE_OFFSET_UNIT_LABELS: Record<
    ScheduleMilestoneOffsetUnit,
    string
> = {
    [ScheduleMilestoneOffsetUnit.days]: "Daily",
    [ScheduleMilestoneOffsetUnit.months]: "Monthly",
    [ScheduleMilestoneOffsetUnit.years]: "Yearly",
};

export const MILESTONE_OFFSET_UNIT_ITEMS: Array<
    PickerItemData<ScheduleMilestoneOffsetUnit>
> = Object.values(ScheduleMilestoneOffsetUnit).map((unit) => ({
    value: unit,
    label: MILESTONE_OFFSET_UNIT_LABELS[unit],
}));

export type MilestoneTimingKind = "on" | "after";

export const MILESTONE_TIMING_ITEMS: Array<
    PickerItemData<MilestoneTimingKind>
> = [
    { value: "on", label: "On" },
    { value: "after", label: "After" },
];

export const MILESTONE_ANCHOR_ITEMS: Array<
    PickerItemData<ScheduleMilestoneAnchor>
> = Object.values(ScheduleMilestoneAnchor).map((anchor) => ({
    value: anchor,
    label: MILESTONE_ANCHOR_LABELS[anchor],
}));

export const TERM_UNIT_LABELS: Record<ScheduleTermUnit, string> = {
    [ScheduleTermUnit.days]: "Daily",
    [ScheduleTermUnit.weeks]: "Weekly",
    [ScheduleTermUnit.months]: "Monthly",
    [ScheduleTermUnit.years]: "Yearly",
};

const TERM_UNIT_NOUN_LABELS: Record<ScheduleTermUnit, string> = {
    [ScheduleTermUnit.days]: "Days",
    [ScheduleTermUnit.weeks]: "Weeks",
    [ScheduleTermUnit.months]: "Months",
    [ScheduleTermUnit.years]: "Years",
};

function buildTermUnitItems(options: {
    method: RecognitionMethod;
    labels: Record<ScheduleTermUnit, string>;
}): Array<PickerItemData<ScheduleTermUnit>> {
    return Object.values(ScheduleTermUnit)
        .filter(
            (unit) =>
                options.method !== RecognitionMethod.dailyProRata ||
                isDailyProRataTermUnit(unit),
        )
        .map((unit) => ({
            value: unit,
            label: options.labels[unit],
        }));
}

export function buildTermUnitNounItems(options: {
    method: RecognitionMethod;
}): Array<PickerItemData<ScheduleTermUnit>> {
    return buildTermUnitItems({
        method: options.method,
        labels: TERM_UNIT_NOUN_LABELS,
    });
}

export interface MilestoneMethodItem {
    disabledReason?: string;
}

/**
 * The Milestone entry of the method picker: offered while the flag is on, kept
 * for a row that already recognizes by milestone, and enabled only on the
 * single row a milestone schedule allows.
 */
export function resolveMilestoneMethodItem(options: {
    isMilestoneAvailable: boolean;
    isMilestone: boolean;
    isOnlyComponent: boolean;
}): MilestoneMethodItem | undefined {
    if (!options.isMilestoneAvailable && !options.isMilestone) {
        return undefined;
    }
    return {
        disabledReason: options.isOnlyComponent
            ? undefined
            : SCHEDULE_MILESTONE_COMPONENT_ALONE_MESSAGE,
    };
}

export function buildRecognitionMethodItems(options: {
    isTermMethodTaken: boolean;
    milestoneItem?: MilestoneMethodItem;
}): Array<PickerItemData<RecognitionMethod>> {
    const termMethods = Object.values(RecognitionMethod).filter(
        isTermRecognitionMethod,
    );
    const items: Array<PickerItemData<RecognitionMethod>> = [
        ...termMethods.map((method) => ({
            value: method,
            label: RECOGNITION_METHOD_LABELS[method],
            disabled: options.isTermMethodTaken,
            disabledReason: options.isTermMethodTaken
                ? SINGLE_TERM_ROW_MESSAGE
                : undefined,
        })),
        {
            value: RecognitionMethod.pointInTime,
            label: RECOGNITION_METHOD_LABELS[RecognitionMethod.pointInTime],
        },
    ];
    if (options.milestoneItem) {
        items.push({
            value: RecognitionMethod.milestone,
            label: RECOGNITION_METHOD_LABELS[RecognitionMethod.milestone],
            disabled: options.milestoneItem.disabledReason !== undefined,
            disabledReason: options.milestoneItem.disabledReason,
        });
    }
    return items;
}

export function buildTermIntervalItems(options: {
    method: RecognitionMethod;
}): Array<PickerItemData<ScheduleTermUnit>> {
    return buildTermUnitItems({
        method: options.method,
        labels: TERM_UNIT_LABELS,
    });
}

const SOURCE_DATE_ANCHORS: RecognitionAnchor[] = [
    RecognitionAnchor.invoiceDate,
    RecognitionAnchor.billDate,
    RecognitionAnchor.transactionDate,
    RecognitionAnchor.termStart,
];

export function isSourceDateAnchor(anchor: RecognitionAnchor): boolean {
    return SOURCE_DATE_ANCHORS.some(
        (sourceDateAnchor) => sourceDateAnchor === anchor,
    );
}

export function toDateAnchorOption(
    anchor: RecognitionAnchor,
): DateAnchorOption {
    return anchor === RecognitionAnchor.specificDate
        ? "specificDate"
        : "sourceDate";
}

export function toRecognitionAnchor(
    option: DateAnchorOption,
    currentAnchor: RecognitionAnchor,
): RecognitionAnchor {
    if (option === "specificDate") {
        return RecognitionAnchor.specificDate;
    }
    if (isSourceDateAnchor(currentAnchor)) {
        return currentAnchor;
    }
    return RecognitionAnchor.transactionDate;
}

export function formatPreviewDateLabel(options: {
    date: Date;
    termUnit: ScheduleTermUnit;
}): string {
    if (options.termUnit === ScheduleTermUnit.days) {
        return format(options.date, "MMM d, yyyy");
    }
    if (options.termUnit === ScheduleTermUnit.weeks) {
        const periodEnd = addDays(options.date, 6);
        return `${format(options.date, "MMM d")}–${format(periodEnd, "MMM d, yyyy")}`;
    }
    if (options.termUnit === ScheduleTermUnit.years) {
        return format(options.date, "yyyy");
    }
    return format(options.date, "MMM yyyy");
}
